import { borderRadius, palette, spaces } from '@kimuno/theme';
import {
	InputBase,
	type SxProps,
	type Theme,
	Typography,
	type TypographyProps,
} from '@mui/material';
import type React from 'react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Mirrors the API's MAX_TITLE_LENGTH.
const MAX_TITLE_LENGTH = 256;

// The input inherits the heading's font, so at rest the title looks like plain
// text; the padding is cancelled by the margin so the text doesn't move.
const inputRootStyles = {
	display: 'flex',
	font: 'inherit',
	letterSpacing: 'inherit',
	color: 'inherit',
	padding: `0 ${spaces.xs}`,
	margin: `0 calc(-1 * ${spaces.xs})`,
	borderRadius: borderRadius.xs,
	// Touch devices keep :hover after a tap; there the field goes straight to its active look.
	'@media (hover: hover)': {
		'&:hover': { backgroundColor: palette.action.hover },
	},
	'&.Mui-focused': {
		boxShadow: `inset 0 0 0 1px ${palette.primary.main}`,
	},
	'& .MuiInputBase-input': {
		height: 'auto',
		padding: 0,
		textOverflow: 'ellipsis',
	},
};

type Props = {
	/** The last saved title. */
	value: string;
	/** Called once per committed change with the trimmed title; a returned promise keeps the committed text shown until it settles. */
	onSave: (title: string) => Promise<unknown> | void;
	variant: TypographyProps['variant'];
	sx?: SxProps<Theme>;
	inputTestId?: string;
};

const InlineEditableTitle: React.FC<Props> = ({ value, onSave, variant, sx, inputTestId }) => {
	const { t } = useTranslation();
	// Null while the field isn't focused, so a new `value` shows at rest but never overwrites typing.
	const [draft, setDraft] = useState<string | null>(null);
	const [committed, setCommitted] = useState<string | null>(null);
	const latestSave = useRef<Promise<unknown> | null>(null);

	const savedTitle = committed ?? value;

	const save = (title: string) => {
		const result = onSave(title);
		if (!(result instanceof Promise)) {
			return;
		}
		setCommitted(title);
		latestSave.current = result;
		const settle = () => {
			if (latestSave.current === result) {
				setCommitted(null);
			}
		};
		result.then(settle, settle);
	};

	const handleBlur = () => {
		const title = (draft ?? '').trim();
		setDraft(null);
		if (title && title !== savedTitle) {
			save(title);
		}
	};

	const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		if (event.key === 'Enter') {
			event.preventDefault();
			event.currentTarget.blur();
		} else if (event.key === 'Escape' && draft !== savedTitle) {
			// Escape cancels only the edit and keeps focus inside a surrounding modal dialog;
			// with nothing left to cancel it propagates, so the next Escape closes the dialog.
			event.stopPropagation();
			setDraft(savedTitle);
		}
	};

	return (
		// Named explicitly: browsers differ on whether a heading's name reads an embedded field's value or its label.
		<Typography variant={variant} sx={sx} aria-label={savedTitle}>
			<InputBase
				value={draft ?? savedTitle}
				onFocus={() => setDraft(savedTitle)}
				onChange={(event) => setDraft(event.target.value)}
				onBlur={handleBlur}
				onKeyDown={handleKeyDown}
				inputProps={{
					'aria-label': t('Title'),
					maxLength: MAX_TITLE_LENGTH,
					'data-testid': inputTestId,
				}}
				sx={inputRootStyles}
			/>
		</Typography>
	);
};

export default InlineEditableTitle;
